import React, { useEffect, useState, useCallback } from "react";
import { Users, Loader2, X, Check, Search, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reporting Hierarchy — HR/Admin only. Lists every active employee with
 * their current manager and lets HR change it with a smart picker.
 */
const ReportingHierarchyCard = ({ authAxios }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // row

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/hr/hierarchy`);
      setRows(r.data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q)
      || (r.username || "").toLowerCase().includes(q)
      || (r.role || "").toLowerCase().includes(q)
      || (r.reporting_manager_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="reporting-hierarchy-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#E83E8C] flex items-center justify-center">
            <Users size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Reporting Hierarchy</h3>
            <p className="text-xs text-[#94a3b8]">
              Change who each employee reports to. HR &amp; Super Admin only.
            </p>
          </div>
        </div>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search staff, role, or current manager…"
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]/50"
          data-testid="hierarchy-search"
        />
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {filtered.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No matches.</p>}
          {filtered.map((r) => (
            <div
              key={r.username}
              className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-[#E83E8C]/30"
              data-testid={`hierarchy-row-${r.username}`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                {r.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1E1B4B] truncate">{r.name}</p>
                <p className="text-[11px] text-[#94a3b8] truncate">
                  @{r.username} · <span className="font-mono">{r.role}</span>
                </p>
              </div>
              <div className="text-right hidden sm:block min-w-[140px]">
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide">Reports to</p>
                <p className="text-xs font-semibold text-[#1E1B4B] truncate">
                  {r.reporting_manager_name || <span className="text-gray-400">— none —</span>}
                </p>
              </div>
              <button
                onClick={() => setEditing(r)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-bold hover:bg-indigo-100"
                data-testid={`change-mgr-${r.username}`}
              >
                Change <ChevronRight size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ChangeManagerModal
          authAxios={authAxios}
          employee={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

const ChangeManagerModal = ({ authAxios, employee, onClose, onSaved }) => {
  const [candidates, setCandidates] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [reportsToRole, setReportsToRole] = useState(null);
  const [selected, setSelected] = useState(employee.reporting_manager || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authAxios.get(`${API_URL}/api/admin/managers/eligible-managers`, { params: { role: employee.role } });
        if (!alive) return;
        setCandidates(r.data?.candidates || []);
        setReportsToRole(r.data?.reports_to_role || null);
      } catch { /* silent */ }
      try {
        const r2 = await authAxios.get(`${API_URL}/api/hr/hierarchy`);
        if (!alive) return;
        setAllStaff((r2.data?.items || []).filter((x) => x.username !== employee.username));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [authAxios, employee.role, employee.username]);

  const list = showAll || candidates.length === 0 ? allStaff : candidates;

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await authAxios.put(
        `${API_URL}/api/admin/managers/${encodeURIComponent(employee.username)}/reporting-manager`,
        { reporting_manager: selected || "" }
      );
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="change-manager-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1E1B4B]">Change Reporting Manager</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="text-xs text-[#94a3b8] mb-3">
          <span className="font-semibold text-[#1E1B4B]">{employee.name}</span>
          {" "}· <span className="font-mono">{employee.role}</span>
        </div>
        {reportsToRole && !showAll && (
          <div className="text-[11px] bg-indigo-50 text-indigo-700 rounded-lg px-2 py-1.5 mb-2">
            Suggested: people with role <span className="font-mono font-bold">{reportsToRole}</span>
          </div>
        )}
        <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">New manager</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl mb-2"
          data-testid="new-manager-select"
        >
          <option value="">— no manager (top-level) —</option>
          {list.map((c) => (
            <option key={c.username} value={c.username}>
              {c.name} · {c.role}
            </option>
          ))}
        </select>
        <div className="flex items-center justify-between text-[11px]">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-indigo-600 font-semibold hover:underline"
            data-testid="toggle-show-all"
          >
            {showAll ? "Show suggested only" : "Show all employees"}
          </button>
          <span className="text-gray-400">{list.length} candidate(s)</span>
        </div>
        {err && <p className="text-xs text-red-500 mt-2">{err}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs rounded-xl border border-gray-200">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-xs rounded-xl bg-[#E83E8C] text-white font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="save-manager-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportingHierarchyCard;
